import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  fetchBackupDeviceListAdmin,
  fetchBackupDeviceListAxios,
  fetchFolderDelete,
} from "../../../api/admin";
import {
  BackupContainer,
  ContentsBody,
} from "../../../style/contents/backup/backup";
import ContentsTopHeader from "../../../components/common/contents.top.header";
import DeviceList from "../../../components/backup/device.list";
import { useAuthStore } from "../../../atoms/user.auth.store";

export interface DeviceItem {
  deviceId: string;
  rootDirectoryList: string[];
  userId: number;
  userAccount: string;
  userName: string;
  userEmail: string;
  isAdmin: boolean;
  originIp: string;
  pcName: string;
}

type DeviceListType = DeviceItem[];

interface Props {
  targetUserId?: number;
  department?: string;
  embedded?: boolean;
  backupKind?: "file" | "db" | "image";
}

const BackupDeviceList = ({ targetUserId, department: groupDepartment, embedded = false, backupKind = "file" }: Props) => {
  const { id: routeId } = useParams<{ id: string }>();
  const id = targetUserId === undefined ? routeId : String(targetUserId);
  const { role } = useAuthStore(); // ✅ role 가져오기
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const encodedDept = params.get("department") || "";
  const department = groupDepartment ?? decodeURIComponent(atob(encodedDept));

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [data, setData] = useState<DeviceListType>([]);
  const [deletingDeviceId, setDeletingDeviceId] = useState<string | null>(null);
  const userId = localStorage.getItem("userId");

  // ✅ 디바이스 목록 불러오기
  useEffect(() => {
    let active = true;
    const loadDeviceList = async () => {
      setIsLoading(true);
      setLoadError(false);
      try {
        if (role === "admin") {
          const response = await fetchBackupDeviceListAdmin(Number(userId));
          const filtered = response.userList.filter(
            (item: any) => item.userId === Number(id),
          );

          if (active) setData(filtered);
        } else {
          // user는 자기 자신의 리스트만 봄
          const response = await fetchBackupDeviceListAxios(Number(userId));
          if (active) setData(response.deviceList || []);
        }
      } catch (err) {
        if (active) setLoadError(true);
      } finally {
        if (active) setIsLoading(false);
      }
    };

    loadDeviceList();
    return () => { active = false; };
  }, [role, id, userId, retry]);

  const handleDeleteDevice = async (device: DeviceItem) => {
    if (role !== "admin" || deletingDeviceId) return;
    if (!window.confirm(`${device.pcName || device.deviceId} 디바이스를 삭제하시겠습니까?`)) return;
    setDeletingDeviceId(device.deviceId);
    try {
      await fetchFolderDelete(userId ?? "", device.userId, device.deviceId);
      setData(previous => previous.filter(item => item.deviceId !== device.deviceId));
      alert("디바이스가 삭제되었습니다.");
    } catch (err) {
      console.error("디바이스 삭제 실패:", err);
      alert("디바이스 삭제 중 오류가 발생했습니다.");
    } finally {
      setDeletingDeviceId(null);
    }
  };

  return (
    <BackupContainer style={embedded ? { height: "auto" } : undefined}>
      {!embedded && <ContentsTopHeader
        title={backupKind === "image" ? "이미지 백업 디바이스 목록" : backupKind === "db" ? "DB 백업 디바이스 목록" : "디바이스 목록"}
        backArrowPath={role === "admin" ? (backupKind === "image" ? "/image/user" : backupKind === "db" ? "/db/user" : "/backup/user") : undefined}
      />}
      <ContentsBody style={embedded ? { height: "auto", padding: 0, overflow: "visible" } : undefined}>
        {isLoading ? <div role="status">디바이스를 불러오는 중입니다...</div> : loadError ? (
          <div role="alert">디바이스 목록을 불러오지 못했습니다. <button type="button" onClick={() => setRetry(value => value + 1)}>다시 시도</button></div>
        ) : <>
        <DeviceList
          data={data}
          ownerId={id}
          individualDevices
          backupKind={backupKind}
          onDelete={handleDeleteDevice}
          deletingDeviceId={deletingDeviceId}
          role={role || ""}
          department={department}
          folderPathPrefix="/backup/folder"
        />
        </>}
      </ContentsBody>
    </BackupContainer>
  );
};

export default BackupDeviceList;
